import { createAxiosDateTransformer } from 'axios-date-transformer';
import { acquireMsalAccessToken, clearMsalAccounts } from '../../features/authentication/msalUtil';
import { baseEndpointUri } from '../config';
import { AxiosError } from 'axios';
import type { ProblemDetails } from './types';

const axiosInstance = createAxiosDateTransformer({
	baseURL: baseEndpointUri
});

let isRedirectingToLogin = false;

/**
 * Clears MSAL state and redirects to login.
 * Returns a never-resolving promise to prevent further error processing
 * while the page navigates.
 */
const redirectToLogin = async (): Promise<never> => {
	if (!isRedirectingToLogin) {
		isRedirectingToLogin = true;
		await clearMsalAccounts();
		window.location.href = '/login';
	}
	// Return a never-resolving promise to stop downstream error handling
	return new Promise(() => {});
};

// Adding auth token by default
axiosInstance.interceptors.request.use(
	async request => {
		try {
			const token = await acquireMsalAccessToken();
			if (!request.headers.Authorization && token) {
				request.headers['Authorization'] = `Bearer ${token}`;
			}
			return request;
		} catch (error) {
			console.error('Failed to acquire MSAL token:', error);
			return redirectToLogin();
		}
	},
	error => {
		return Promise.reject(error);
	}
);

axiosInstance.interceptors.response.use(
	response => {
		return response;
	},
	(error: AxiosError<ProblemDetails>) => {
		if (error.response?.status === 401) {
			return redirectToLogin();
		}

		if (!error.response) {
			return Promise.reject({ isNetworkError: true, detail: 'Network error' });
		}

		if (process.env.NODE_ENV === 'development') {
			console.error('API Error:', error);
		}
		return Promise.reject({ ...error, isNetworkError: false });
	}
);

export const axios = axiosInstance;
